import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Instagram, Youtube, Music2, Globe, Play, Pause, Flame, Eye, Radio, MapPin, FileDown } from "lucide-react";
import { BASE } from "@/lib/api";
import { BookingDialog } from "@/components/fo/BookingDialog";
import { EventRow } from "@/components/fo/Events";
import { api, media, deviceId, fmtDate } from "@/lib/api";
import { Badges } from "@/components/fo/Bits";
import { Parallax, CountUp } from "@/components/fo/Motion";

const ICONS = { instagram: Instagram, youtube: Youtube, spotify: Music2, tiktok: Music2, soundcloud: Music2, apple_music: Music2, bandcamp: Music2, website: Globe };
const LABELS = { instagram: "Instagram", youtube: "YouTube", spotify: "Spotify", tiktok: "TikTok", soundcloud: "SoundCloud", apple_music: "Apple Music", bandcamp: "Bandcamp", website: "Website" };

const TrackRow = ({ t, active, onPlay }) => (
  <div data-testid={`artist-track-${t.id}`} className="flex items-center gap-4 border-b border-white/5 py-3">
    <button onClick={onPlay} data-testid={`artist-track-play-${t.id}`} className="flex h-10 w-10 items-center justify-center rounded-full border border-[#00F5D4]/50 text-[#00F5D4] transition-colors hover:bg-[#00F5D4] hover:text-[#07090E]">
      {active ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
    </button>
    <div className="min-w-0 flex-1"><div className="truncate font-display font-bold">{t.title}</div><div className="font-mono text-xs uppercase text-slate-500">{t.genre}</div></div>
    <span className="font-mono text-xs text-slate-500">{Math.floor(t.duration / 60)}:{String(Math.round(t.duration % 60)).padStart(2, "0")}</span>
  </div>
);

const Stat = ({ label, value, icon: Icon, testid }) => (
  <div className="panel p-4" data-testid={testid}>
    <Icon className="h-4 w-4 text-[#00F5D4]" />
    <div className="led mt-2 text-3xl font-bold">{typeof value === "number" ? <CountUp value={value} /> : value ?? "--"}</div>
    <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
  </div>
);

export default function ArtistPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [events, setEvents] = useState([]);
  const audio = useRef(null);

  useEffect(() => {
    api.get(`/artists/${slug}`).then((r) => setA(r.data)).catch(() => setErr(true));
    api.get(`/artists/${slug}/events`).then((r) => setEvents(r.data)).catch(() => {});
    api.post(`/artists/${slug}/visit`, { device_id: deviceId(), play_id: params.get("play") }).catch(() => {});
    return () => audio.current?.pause();
  }, [slug]); // eslint-disable-line

  const preview = (t) => {
    if (!audio.current) audio.current = new Audio();
    if (playingId === t.id) { audio.current.pause(); setPlayingId(null); return; }
    audio.current.src = media(t.audio);
    audio.current.play();
    setPlayingId(t.id);
  };

  if (err) return <div className="pt-40 text-center text-slate-400" data-testid="artist-not-found">Artist not found.</div>;
  if (!a) return <div className="pt-40 text-center font-mono text-slate-500">Loading…</div>;
  return (
    <div data-testid="artist-page">
      <section className="relative min-h-[80vh] overflow-hidden">
        {a.photo && <Parallax speed={-0.15} className="absolute -inset-y-40 inset-x-0"><img src={media(a.photo)} alt={a.stage_name} className="h-full w-full object-cover" /></Parallax>}
        <div className="absolute inset-0 bg-gradient-to-t from-[#07090E] via-[#07090E]/60 to-[#07090E]/10" />
        <div className="relative mx-auto flex min-h-[80vh] max-w-7xl flex-col justify-end px-4 pb-14 pt-28 md:px-8">
          <span className="label-mono fade-up">{a.role_type} · {a.genre}</span>
          <h1 data-testid="artist-name" className="fade-up d1 mt-3 font-display text-6xl font-black uppercase leading-[.85] tracking-tight sm:text-8xl lg:text-9xl">{a.stage_name}</h1>
          {a.city && <p className="fade-up d2 mt-4 flex items-center gap-2 text-slate-300"><MapPin className="h-4 w-4" />{a.city}</p>}
          <Badges badges={a.badges} plan={a.plan} className="fade-up d3 mt-5" />
          <div className="fade-up d4 mt-6 flex flex-wrap gap-3">
            {Object.entries(a.socials || {}).filter(([, v]) => v).map(([k, v]) => {
              const I = ICONS[k] || Globe;
              return <a key={k} href={v} target="_blank" rel="noreferrer" data-testid={`artist-social-${k}`} className="btn-ghost !px-4 !py-2 text-sm"><I className="h-4 w-4" />{LABELS[k] || k}</a>;
            })}
          </div>
        </div>
      </section>
      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-16 md:px-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <div className="label-mono mb-4">Bio</div>
          <p className="whitespace-pre-line text-lg leading-relaxed text-slate-300" data-testid="artist-bio">{a.bio}</p>
          <div className="label-mono mb-2 mt-12">Tracks</div>
          {a.tracks.map((t) => <TrackRow key={t.id} t={t} active={playingId === t.id} onPlay={() => preview(t)} />)}
          {events.length > 0 && <div data-testid="artist-events">
            <div className="label-mono mb-2 mt-12">Upcoming shows</div>
            {events.map((e) => <EventRow key={e.id} e={e} />)}
          </div>}
          {a.articles?.length > 0 && <>
            <div className="label-mono mb-4 mt-12">In the magazine</div>
            {a.articles.map((x) => <Link key={x.id} to={`/blog/${x.slug}`} className="block border-b border-white/5 py-3 font-display font-bold hover:text-[#00F5D4]">{x.title} <span className="font-mono text-xs text-slate-500">· {fmtDate(x.created_at)}</span></Link>)}
          </>}
        </div>
        <div className="lg:col-span-5">
          <div className="label-mono mb-4">Live on Fuorionda</div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Plays on air" value={a.stats.plays} icon={Radio} testid="artist-stat-plays" />
            <Stat label="Flames" value={a.stats.flames} icon={Flame} testid="artist-stat-flames" />
            <Stat label="Blind Flames" value={a.stats.blind_flames} icon={Eye} testid="artist-stat-blind" />
            <Stat label="Blind / Fan rank" value={`${a.blind_rank ? "#" + a.blind_rank : "–"} / ${a.fan_rank ? "#" + a.fan_rank : "–"}`} icon={Flame} testid="artist-stat-rank" />
          </div>
          <Link to="/radio" className="btn-hot mt-6 w-full" data-testid="artist-listen-radio">Listen to the radio</Link>
          <a href={`${BASE}/api/artists/${a.slug}/presskit.pdf`} className="btn-ghost mt-3 w-full" data-testid="artist-presskit-download"><FileDown className="h-4 w-4" />Download press kit (PDF)</a>
          <BookingDialog artist={a} />
        </div>
      </section>
    </div>
  );
}
